<template>
	<section :id="props.sectionId" ref="sectionRef" class="partners">
		<h2 class="partners__title heading h2">
			{{ $t('pages.main.sections.partners.title') }}
		</h2>
		<!-- <div class="partners__container container"> -->
		<div class="partners__content">
			<div class="partners__group">
				<div
					v-for="({ name, img }, i) in partnersSortedList"
					:key="i"
					class="partners__item"
					:class="name">
					<img
						class="partners__img"
						:src="img"
						loading="lazy"
						decoding="async"
						width="150"
                        height="100"
                        fit="contain"
                        background="transparent"
						:alt="name" />
				</div>
			</div>
			<button
				v-if="isMobile && props.partners.length > partnersSortedList.length"
				class="partners__show-more"
				@click="showAll = true">
				{{ $t('basics.showMore') }}
			</button>
		</div>
		<!-- </div> -->
		<div id="footerAnchor" class="footer__anchor"></div>
	</section>
</template>

<script setup lang="ts">
	import type { PartnerItem } from '~/constants/partners'

	const { $gsap: gsap } = useNuxtApp()

	const props = withDefaults(
		defineProps<{
			partners: PartnerItem[]
			sectionId?: string
			mobileLimit?: number
		}>(),
		{
			sectionId: 'partners',
			mobileLimit: 6,
		}
	)

	const showAll = ref(false)
	const isMobile = ref(false)
	const sectionRef = ref<HTMLElement | null>(null)

	const updateIsMobile = () => {
		isMobile.value = window.innerWidth < 768
	}

	watch(
		() => props.partners,
		() => {
			showAll.value = false
		}
	)

	const partnersSortedList = computed(() => {
		return isMobile.value && !showAll.value ? props.partners.slice(0, props.mobileLimit) : props.partners
	})

	onMounted(() => {
		updateIsMobile()
		window.addEventListener('resize', updateIsMobile)

		const partnerItems = sectionRef.value?.querySelectorAll('.partners__item')
		if (!partnerItems?.length) return

		gsap.set(partnerItems, {
			x: () => gsap.utils.random(-window.innerWidth / 2, window.innerWidth / 2),
			y: () => gsap.utils.random(-100, 100),
			rotation: () => gsap.utils.random(-45, 45),
			scale: () => gsap.utils.random(0.2, 1.2),
		})

		gsap
			.timeline({
				scrollTrigger: {
					trigger: sectionRef.value,
					start: 'top bottom',
					end: 'top top ',
					scrub: true,
					toggleActions: 'play none none reverse',
				},
			})
			.to(partnerItems, {
				x: '0vw',
				y: 0,
				rotation: 0,
				scale: 1,
				opacity: 1,
				ease: 'back.inOut(1.7)',
			})
	})

	onBeforeUnmount(() => {
		window.removeEventListener('resize', updateIsMobile)
	})
</script>

<style scoped lang="scss">
	.partners {
		position: relative;
		z-index: 1;

		// scroll-margin-top: 80px;
		padding: 64px 0;
		overflow-x: clip;
		background-color: #f5f5f5;
		border-radius: 0 0 50px 50px;

		@media (width <= 767.98px) {
			border-radius: 0 0 20px 20px;
		}
		&__title {
			position: relative;
			z-index: 1;
			text-align: center;
			text-transform: uppercase;
		}
		&__content {
			min-width: 0;
			padding: 8px;
			margin-top: 32px;
		}
		&__group {
			display: grid;
			grid-template-columns: repeat(5, 1fr);
			gap: 8px;

			@media (width <=1198.98px) {
				grid-template-columns: repeat(4, 1fr);
			}

			@media (width <=991.98px) {
				grid-template-columns: repeat(2, 1fr);
				gap: 8px;
			}

			// &:hover {
			// 	.partners__item {
			// 		opacity: 0.5;
			// 		filter: grayscale(1);
			// 		&:hover {
			// 			opacity: 1;
			// 			filter: grayscale(0);
			// 		}
			// 	}
			// }
		}
		&__item {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 32px;
			background-color: var(--white);
			border-radius: 20px;
			box-shadow: 0 4px 8px rgb(0 0 0 / 10%);
			transition:
				0.4s opacity,
				0.4s filter;
		}
		&__img {
			object-fit: contain;
		}
		&__show-more {
			display: block;
			padding: 12px 32px;
			margin: 24px auto 0;
			font-weight: 600;
			background: #ffffff;
			border-radius: 8px;
			box-shadow: 0 4px 8px rgb(0 0 0 / 10%);
		}
	}
	.footer__anchor {
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 1px;
		background-color: transparent;
	}
</style>
